import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { domainRouterClient } from '../../api/domainRouterClient';
import { asArray, asRecord } from './graphql';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type ParentPortalSnapshot = {
  studentProfiles: Record<string, unknown>[];
  documents: Record<string, unknown>[];
  calendarEvents: Record<string, unknown>[];
  transactions: Record<string, unknown>[];
  resources: Record<string, unknown>[];
  messageThreads: Record<string, unknown>[];
  notifications: Record<string, unknown>[];
  fetchedAt: number;
};

type ParentPortalLiveState = {
  status: AsyncStatus;
  error: string | null;
  snapshot: ParentPortalSnapshot | null;
};

type ParentPortalSnapshotArgs = {
  actorId?: string;
  familyId?: string;
  applicationId?: string;
  academicYear?: string;
};

const initialState: ParentPortalLiveState = {
  status: 'idle',
  error: null,
  snapshot: null,
};

const compactInput = (input: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(input).filter(([, value]) => value != null && `${value}`.trim() !== '')
  );

export const fetchParentPortalSnapshot = createAsyncThunk(
  'parentPortalLive/fetchSnapshot',
  async (args: ParentPortalSnapshotArgs = {}): Promise<ParentPortalSnapshot> => {
    const portalInput = compactInput({
      familyId: args.familyId,
      applicationId: args.applicationId,
    });
    const threadInput = compactInput({
      familyId: args.familyId,
      participantId: args.actorId,
      applicationId: args.applicationId,
    });
    const notificationInput = compactInput({
      actorId: args.actorId,
      familyId: args.familyId,
      includeRead: true,
      limit: 50,
    });
    const transactionInput = compactInput({
      familyId: args.familyId,
      academicYear: args.academicYear,
    });

    const [
      studentProfilesRaw,
      documentsRaw,
      calendarEventsRaw,
      resourcesRaw,
      messageThreadsRaw,
      notificationsRaw,
    ] = await Promise.all([
      domainRouterClient.query('listPortalStudentProfiles', { input: JSON.stringify(portalInput) }),
      domainRouterClient.query('listPortalDocuments', { input: JSON.stringify(portalInput) }),
      domainRouterClient.query('listPortalCalendarEvents', { input: JSON.stringify(portalInput) }),
      domainRouterClient.query('listPortalResources', { input: JSON.stringify({}) }),
      domainRouterClient.query('listMessageThreads', { input: JSON.stringify(threadInput) }),
      domainRouterClient.query('listNotifications', { input: JSON.stringify(notificationInput) }),
    ]);

    const transactionsRaw = args.familyId
      ? await domainRouterClient.query('listPortalTransactions', { input: JSON.stringify(transactionInput) })
      : { items: [] };

    return {
      studentProfiles: asArray<Record<string, unknown>>(asRecord(studentProfilesRaw).items),
      documents: asArray<Record<string, unknown>>(asRecord(documentsRaw).items),
      calendarEvents: asArray<Record<string, unknown>>(calendarEventsRaw),
      transactions: asArray<Record<string, unknown>>(asRecord(transactionsRaw).items),
      resources: asArray<Record<string, unknown>>(resourcesRaw),
      messageThreads: asArray<Record<string, unknown>>(asRecord(messageThreadsRaw).items),
      notifications: asArray<Record<string, unknown>>(asRecord(notificationsRaw).items),
      fetchedAt: Date.now(),
    };
  }
);

const parentPortalLiveSlice = createSlice({
  name: 'parentPortalLive',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchParentPortalSnapshot.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchParentPortalSnapshot.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.snapshot = action.payload;
      })
      .addCase(fetchParentPortalSnapshot.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to load parent portal snapshot.';
      });
  },
});

export const parentPortalLiveReducer = parentPortalLiveSlice.reducer;

